import { useForm, FormProvider } from 'react-hook-form';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { modalFormSchema, type ModalFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useEffect } from 'react';
import { MediaField } from '../MediaField';
import { Posts, PostStatus } from '@/model';
import { usePutPostsMutation } from '@/services/queries/posts';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PostsEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Posts;
  onClose: () => void;
  onDone?: () => void;
}

export const PostsEditModal = ({ isOpen, onClose, item, onDone }: PostsEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const putItemMutation = usePutPostsMutation();

  const form = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: '',
      identifier: '',
      content: '',
      posts_media: [],
      status: PostStatus.DRAFT,
      disable_comments: false,
      enable_home: true,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
    reset,
    control,
  } = form;

  const formValues = watch();

  const renameFile = (file: File, newName: string): File => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handleCreate = (data: ModalFormData) => {
    if (!item) return;

    const postsMediaMeta: { uuid?: string; image_url?: string }[] = [];
    const mediaFiles: Record<string, File> = {};

    data.posts_media.forEach(mediaItem => {
      const uuid = mediaItem.uuid ?? uuidv4();
      const fileToSend = mediaItem.file_cropped ?? mediaItem.file;

      if (fileToSend) {
        const ext = fileToSend.name.split('.').pop();
        const renamedFile = renameFile(fileToSend, `${uuid}.${ext}`);
        mediaFiles[uuid] = renamedFile;
        postsMediaMeta.push({ uuid });
      } else {
        postsMediaMeta.push({
          uuid: mediaItem.uuid,
          image_url: mediaItem.image_url,
        });
      }
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      identifier: data.identifier,
      content: data.content,
      status: data.status,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      posts_media: postsMediaMeta,
      ...mediaFiles,
    };

    putItemMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({ title: t('pages.application_posts.components.posts_edit_modal.posts_edit_success'), status: 'success' });
          onDone?.();
          reset();
        },
        onError: (e: any) => {
          toast({
            title: e?.message || t('pages.application_posts.components.posts_edit_modal.posts_edit_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  useEffect(() => {
    if (!item) return;

    setValue('team_uuid', item.team_uuid);
    setValue('identifier', item.identifier);
    setValue('content', item.content);
    setValue('status', item.status);
    setValue('disable_comments', item.disable_comments);
    setValue('enable_home', item.enable_home);

    if (item?.posts_media?.length) {
      item?.posts_media?.forEach((item, index) => {
        if (item) {
          fetch(item.image_url)
            .then(() => {
              setValue(`posts_media.${index}.image_url`, item.image_url);
              setValue(`posts_media.${index}.uuid`, item.uuid);
            })
            .catch(err => console.log(err));
        }
      });
    }
  }, [setValue, item]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(handleCreate)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_posts.components.posts_edit_modal.posts_edit_title')}
            <Flex gap={2} alignItems='center'>
              <Text fontSize='14px' fontWeight='normal'>
                {t('pages.application_posts.components.posts_edit_modal.posts_edit_published_label')}
              </Text>
              <Switch
                isChecked={formValues.status === PostStatus.PUBLISHED}
                onChange={event => setValue('status', event.target.checked ? PostStatus.PUBLISHED : PostStatus.NOT_PUBLISHED)}
                display='flex'
                alignItems='center'
                size='sm'
              />
            </Flex>
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_posts.components.posts_edit_modal.posts_edit_team_label')}
                placeholder={t('pages.application_posts.components.posts_edit_modal.posts_edit_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />

              <InputField
                withoutFloating
                label={t('pages.application_posts.components.posts_edit_modal.posts_edit_identifier_label')}
                isDisabled={item.status !== PostStatus.DRAFT}
                helperText={t('pages.application_posts.components.posts_edit_modal.posts_edit_identifier_helper_text')}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                withoutFloating
                isDisabled={item.status !== PostStatus.DRAFT}
                label={t('pages.application_posts.components.posts_edit_modal.posts_edit_content_label')}
                helperText={t('pages.application_posts.components.posts_edit_modal.posts_edit_content_helper_text')}
                maxLength={500}
                {...register('content')}
                rootProps={{ isInvalid: !!errors?.content, errorMessage: errors?.content?.message }}
              />

              <FormProvider {...form}>
                <MediaField isEdit />
              </FormProvider>

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_posts.components.posts_edit_modal.posts_edit_disable_comments_label')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_posts.components.posts_edit_modal.posts_edit_enable_home_label')}
              </Switch>              
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Button type='button' variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_posts.components.posts_edit_modal.posts_edit_button_cancel')}
            </Button>
            <Button type='submit'>{t('pages.application_posts.components.posts_edit_modal.posts_edit_button_save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </>
  );
};
